/* KANON `Programy v2 - D3 (0.8)` — the pattern week, transcribed.
 *
 * Promoted out of `pages/plan-library` with its component on 23 September 2026. The section's
 * inset is the HOST's: the inspector pads it 19px from the pane's edge, the create dialog's preview
 * stands it flush in its own column. Declaring it here as well would be settled by chunk order.
 *
 * Read off the rendered specimen at 1512x1000:
 *   section  0 19 padding, an 8px column, 108 tall including its heading
 *   heading  `shared/ui/paneHeading`, 16 / 700 / --text (owner, 4 November 2026)
 *   grid     6.5 between tiles, each column a 5px stack of caption over tile
 *   caption  12 / 500, sentence case, --text-2 on a day and --text-3 on a free slot
 *   tile     `--h-pl-tile` tall (57 since 4 November 2026: whatever the section leaves under its
 *            title and the caption), radius 8, 8 padding, a 2.5px column stacked from its FOOT
 *            a day: --bg-inset inside a 1px --accent frame
 *            free: no box at all, and the word in --text-3
 *   name     13 / 600 on a day, 13 / 500 on a free slot
 *   count    12 / 500, --text-3, tabular
 *
 * ── The owner's polish of 4 November 2026 ────────────────────────────────────────────────────
 * The category's 3px band across a training tile's top went, and the tile's frame took the ember
 * instead: the band repeated the chip on the plate above in a third place, and the frame marks the
 * one thing a pattern week is read for — which days train. A free day lost its dashed box: a box
 * drawn around nothing was the loudest object on a rest day, and `wolne` says it in words. The
 * eyebrow and the captions lost their tracked capitals.
 *
 * ── The tile's two lines, and what each of them names ────────────────────────────────────────
 * The canon puts the plan's own name for the day in the title and the weekday in the 8px caption
 * above it. On 16 September 2026 that was inverted — the title became the WEEKDAY and the plan's
 * name dropped to a third line — because a plan whose days are called `Squat` and `Bench` read as a
 * list of exercises. **Withdrawn on 17 September 2026**, deviation D31 with it: the owner put the
 * result beside the canon and the inversion had printed the weekday TWICE, once in the caption and
 * once as the title, leaving the only line that says anything about the plan as the small grey one
 * underneath. So the title is the day's own name again, the caption above it is the slot, and the
 * third line is gone rather than emptied.
 *
 * A day the trainer never named falls back to the weekday, which is the one case where naming the
 * day after the day is the true thing to do.
 *
 * ── The section holds its height whether the read has answered or not ────────────────────────
 * `--h-pl-week` is on `.section`, so the two sections below never move. The skeleton bars stand
 * INSIDE the tiles that are already there — the gate hides the bars, never the box.
 *
 * **Every text leaf states its line-height.** `base.css` puts `--lh-relaxed` on `body` and the
 * specimen's body carries none, so an unstated leaf renders about 2px taller than the canon.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

.section {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-2);
  height: var(--h-pl-week);
}

/* Equal tracks rather than `1fr` columns: a self-paced cycle can hold more or fewer than seven
 * days, and `flex: 1` on each column divides whatever the row has between however many there are.
 * `min-width: 0` is what lets a name ellipsise instead of widening its own tile. */
.grid {
  display: flex;
  flex: 1;
  gap: var(--space-6-5px);
  min-height: 0;
}

.column {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-5px);
  min-width: 0;
}

/* The caption's box is stated rather than left to its own text: while the read is open it holds a
 * skeleton bar instead of a word, and a box that collapsed to the bar would lift every tile the
 * moment the answer landed. The minimum IS the word's line, `--type-label-lh`, stated as the
 * line-height too, so the two states are one height by construction rather than by font metric. */
.dow {
  display: block;
  min-height: var(--type-label-lh);
  font-size: var(--type-label);
  font-weight: var(--fw-medium);
  line-height: var(--type-label-lh);
  color: var(--text-2);
}

/* The caption dims with the tile under it. The word inside the tile says the same thing, so the
 * state is never carried by the colour alone. */
.dowFree {
  color: var(--text-3);
}

/* Stacked from the FOOT, which is what puts a one-line `wolne` where the specimen puts it: the
 * tile's own content grows upward from its bottom padding. */
.tile {
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2-5px);
  justify-content: flex-end;
  min-height: 0;
  padding: var(--space-2);
  overflow: hidden;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-8px);
}

/* A day that trains. The accent frame is the pattern week's one coloured mark and it means one
 * thing: training happens here. The wait's tiles keep the neutral `--border-2` above, because a
 * wait must not claim a day trains before the plan has said so. */
.tileDay {
  border-color: var(--accent);
}

/* No box around a rest day. The border stays, in the ground's own colour through a custom property
 * — Stylelint's allowlist reads `transparent` as a literal — so the tile keeps its geometry and
 * `wolne` sits exactly where a day's name does. */
.tileFree {
  --tile-free-edge: transparent;

  background: transparent;
  border-color: var(--tile-free-edge);
}

.name {
  overflow: hidden;
  font-size: var(--type-control);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-heading);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.free {
  font-weight: var(--fw-medium);
  color: var(--text-3);
}

/* The plan's own name for the day. `--text-2` rather than `--text-3`: it is the trainer's word and
 * the thing they recognise the day by, so it outranks the count under it — but it no longer outranks
 * the weekday, which is the whole of deviation D31. */
.count {
  overflow: hidden;
  font-size: var(--type-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-3);
  text-overflow: ellipsis;
  white-space: nowrap;

  /* The specimen's own `exSt.fontVariantNumeric`, and the app's standing rule that numbers are
   * tabular. Seven tiles in a row whose figures are 7, 8 and 6 wander by a fraction of a pixel
   * without it — read off `kanonDiff`, not off the screen. */
  font-variant-numeric: tabular-nums;
}

/* ── The wait ───────────────────────────────────────────────────────────────────────────────── */

/* Each bar stands where its own leaf stands, at that leaf's height, so the tiles are the same shape
 * before and after the answer. */
.skelDay {
  width: 14px;
  height: 8px;
}

.skelName {
  width: 62%;
  height: 8px;
}

.skelCount {
  width: 34%;
  height: 7px;
}
